import { KNOWN_LAYOUTS } from "./knownLayouts.ts";
import { isSimplePrintableKey } from "./shared.ts";
import type { NavigatorWithKeyboard } from "./types.ts";

const LAYOUT_STORAGE_KEY = "keycapture-detected-layout";

/**
 * Cache of code → base key mappings learned from keystrokes.
 * Used as fallback when Keyboard Layout API is not available (Firefox).
 * Populated via layout detection or individual key learning.
 */
const learnedLayoutMap = new Map<string, string>();

/** Track which layout has been detected (null = not yet detected) */
let detectedLayout: string | null = null;

/** Track which layouts are still possible candidates (for progressive elimination) */
let possibleLayouts: Set<string> | null = null;

/**
 * Applies a detected layout - populates the learned map and persists to localStorage.
 */
const applyLayout = (layoutName: string): void => {
  const layoutMap = KNOWN_LAYOUTS[layoutName];
  if (!layoutMap) return;

  detectedLayout = layoutName;
  possibleLayouts = null;

  // Populate the entire learned map with this layout
  for (const [layoutCode, layoutKey] of Object.entries(layoutMap)) {
    learnedLayoutMap.set(layoutCode, layoutKey);
  }

  // Persist to localStorage for instant detection on reload
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, layoutName);
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
};

/**
 * Restores layout from localStorage if available.
 */
const restoreLayoutFromStorage = (): boolean => {
  try {
    const stored = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (stored && KNOWN_LAYOUTS[stored]) {
      applyLayout(stored);
      return true;
    }
  } catch {
    // localStorage may be unavailable
  }
  return false;
};

/**
 * Uses locale hints to guess a likely default layout.
 * Returns the most likely layout name based on navigator.language.
 */
const guessLayoutFromLocale = (): string | null => {
  if (typeof navigator === "undefined") return null;

  const lang = navigator.language?.toLowerCase() ?? "";

  // Map common language codes to likely layouts
  if (lang.startsWith("fr")) return "azerty";
  if (lang.startsWith("de") || lang.startsWith("cs") || lang.startsWith("sk")) return "qwertz";
  // Most other languages use QWERTY or a variation close enough
  return null;
};

/**
 * Attempts to detect the keyboard layout from a single keypress.
 * Uses progressive elimination - each keypress narrows down possible layouts.
 * Returns true if layout was detected and full map was populated.
 *
 * Also verifies existing layout detection - if a keystroke doesn't match
 * the detected layout, resets and re-detects (handles layout changes).
 */
const tryDetectLayout = (code: string, key: string): boolean => {
  const lowerKey = key.toLowerCase();

  // If we already detected a layout, verify this keystroke matches
  if (detectedLayout) {
    const expectedKey = KNOWN_LAYOUTS[detectedLayout]?.[code];
    if (expectedKey && expectedKey !== lowerKey) {
      // Mismatch! User may have changed keyboard layout - reset and re-detect
      detectedLayout = null;
      possibleLayouts = null;
      learnedLayoutMap.clear();
      try {
        localStorage.removeItem(LAYOUT_STORAGE_KEY);
      } catch {
        // localStorage may be unavailable
      }
      // Continue to re-detect below
    } else {
      // Matches expected - layout still valid
      return true;
    }
  }

  // Initialize possible layouts on first call
  if (possibleLayouts === null) {
    possibleLayouts = new Set(Object.keys(KNOWN_LAYOUTS));
  }

  // Eliminate layouts that don't match this code→key mapping
  for (const layoutName of [...possibleLayouts]) {
    const layoutMap = KNOWN_LAYOUTS[layoutName];
    if (layoutMap[code] && layoutMap[code] !== lowerKey) {
      possibleLayouts.delete(layoutName);
    }
  }

  if (possibleLayouts.size === 1) {
    // Unique match - we've identified the layout!
    const layoutName = [...possibleLayouts][0];
    applyLayout(layoutName);
    return true;
  }

  if (possibleLayouts.size === 0) {
    // No known layout matches - might be an uncommon layout
    // Reset and just learn individual keys
    possibleLayouts = null;
    learnedLayoutMap.set(code, lowerKey);
    return false;
  }

  // Multiple layouts still possible - store this key and continue
  learnedLayoutMap.set(code, lowerKey);
  return false;
};

/**
 * Records a code → key mapping for future lookups.
 * First attempts layout detection, then falls back to individual learning.
 */
export const learnKeyMapping = (code: string, key: string): void => {
  if (!isSimplePrintableKey(key)) return;
  if (!code.startsWith("Key")) return;

  // Try to detect full layout from this keypress
  if (tryDetectLayout(code, key)) return;

  // If layout not yet determined, store individual mapping
  learnedLayoutMap.set(code, key.toLowerCase());
};

/**
 * Gets a previously learned key mapping for a code.
 */
const getLearnedMapping = (code: string): string | undefined => {
  return learnedLayoutMap.get(code);
};

/**
 * Gets the base key-cap character for a physical key code.
 * Tries Keyboard Layout API first, then falls back to learned/detected layout.
 */
export const getKeyCap = async (
  code: string,
  currentKey: string,
  hasAltModifier: boolean,
): Promise<string | undefined> => {
  // Try Keyboard Layout API first (Chrome, Edge)
  const nav = navigator as NavigatorWithKeyboard;

  if (nav.keyboard?.getLayoutMap) {
    try {
      const layoutMap = await nav.keyboard.getLayoutMap();

      const layoutKey = layoutMap.get(code);

      if (layoutKey) {
        return layoutKey;
      }
    } catch {
      // Fall through to other methods
    }
  }

  // If no Alt modifier, the current key IS the base key - learn and use it
  if (!hasAltModifier && isSimplePrintableKey(currentKey)) {
    learnKeyMapping(code, currentKey);
    return currentKey;
  }

  // Try learned/detected mapping (Firefox fallback)
  const learned = getLearnedMapping(code);

  if (learned) {
    return learned;
  }

  // No layout info available
  return undefined;
};

/**
 * Global keyboard listener for background learning.
 * Captures the keyboard layout passively from ANY typing on the page.
 * Uses layout fingerprinting to detect layout from a few key presses.
 */
export const beginLearningKeyboardLayout = (): void => {
  if (typeof window === "undefined") return;

  // Check if Keyboard Layout API is available - if so, no need for background learning
  if ((navigator as NavigatorWithKeyboard).keyboard?.getLayoutMap) return;

  // Only initialize once
  if (window.__keyCaptureListenerInit) return;
  window.__keyCaptureListenerInit = true;

  // Try to restore layout from localStorage for immediate use
  // But still set up the listener to detect layout changes
  restoreLayoutFromStorage();

  // Apply locale-based hint as initial guess if no layout restored
  // (will be confirmed/corrected by actual typing)
  const localeGuess = guessLayoutFromLocale();

  if (localeGuess) {
    // Don't fully apply - just set as the only possible layout to start
    // First keypress will confirm or invalidate
    possibleLayouts = new Set([localeGuess, "qwerty"]); // Include QWERTY as fallback
  }

  // Listen to all key presses on the document
  document.addEventListener(
    "keydown",
    (e: KeyboardEvent) => {
      // Only learn from key presses without modifiers
      // (modifiers can change character output)
      if (!e.altKey && !e.metaKey && !e.ctrlKey && !e.shiftKey && isSimplePrintableKey(e.key)) {
        learnKeyMapping(e.code, e.key);
      }
    },
    { passive: true, capture: true },
  );
};
